import {
  EmptyState,
  Sidebar,
  SidebarProps,
  SkeletonLoader,
} from '@payaca/ambrosia';
import { FC, useState } from 'react';

import AddEditProposalLineItemContent from '@/ui/components/addEditProposalLineItem/AddEditProposalLineItemContent';
import { TFormState } from '@/ui/components/addEditProposalLineItem/formState';
import useProposalLineItemEditor from '@/ui/components/addEditProposalLineItem/useProposalLineItemEditor';
import SidebarSplit from '@/ui/components/sidebarSplit/SidebarSplit';

type TOwnProps = {
  rootPage?: 'proposal' | 'invoice';
  allowProposalLineItemSelectionType?: boolean;
  proposalLineItemId?: string;
  onRemoveSuccess?: () => void;
};

type TProps = Omit<SidebarProps, 'title'> &
  TOwnProps & {
    onSaveSuccess?: () => void;
  };

const EditProposalLineItemSidebar: FC<TProps> = (props) => {
  const {
    rootPage,
    proposalLineItemId,
    onRemoveSuccess,
    onSaveSuccess,
    allowProposalLineItemSelectionType,
    onClose,
    ...rest
  } = props;

  /**
   * State
   */
  const [isLinkMaterialSidebarOpen, setIsLinkMaterialSidebarOpen] =
    useState(false);
  const [shouldWarnUserOnClose, setShouldWarnUserOnClose] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    proposalLineItemData,
    isLoading,
    isNotFound,
    onSubmit,
    onThumbnailDelete,
    onDelete,
  } = useProposalLineItemEditor({
    proposalLineItemId,
    onSaveSuccess,
    onRemoveSuccess,
  });

  const handleSubmit = async (state: TFormState) => {
    setIsSubmitting(true);
    try {
      await onSubmit(state);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sidebar
      title={proposalLineItemId ? 'Edit item' : 'Add item'}
      warnUserOnClose={shouldWarnUserOnClose && !isSubmitting}
      warnUserContent={{
        body: 'The changes to this item are not saved and will be lost.',
        confirm: 'Discard changes',
      }}
      behind={isLinkMaterialSidebarOpen}
      onClose={isSubmitting ? undefined : onClose}
      size="lg"
      {...rest}
    >
      {isNotFound && (
        <EmptyState
          className="py-8"
          iconName="alert-triangle.3"
          text="This item has been removed"
        />
      )}
      {isLoading && <LineItemSidebarSkeleton />}
      {!isNotFound && !isLoading && (
        <AddEditProposalLineItemContent
          saveMode="button"
          rootPage={rootPage}
          isSaving={isSubmitting}
          proposalLineItemId={proposalLineItemId}
          proposalLineItemData={proposalLineItemData}
          isLinkMaterialSidebarOpen={isLinkMaterialSidebarOpen}
          setIsLinkMaterialSidebarOpen={setIsLinkMaterialSidebarOpen}
          onSubmit={handleSubmit}
          onThumbnailDelete={onThumbnailDelete}
          onDelete={onDelete}
          allowProposalLineItemSelectionType={
            allowProposalLineItemSelectionType
          }
          setIsDirty={setShouldWarnUserOnClose}
        />
      )}
    </Sidebar>
  );
};

const LineItemSidebarSkeleton: FC = () => (
  <SidebarSplit
    leftTab={{ label: 'Item details' }}
    rightTab={{ label: 'Linked materials' }}
  >
    <div className="flex flex-col gap-4 p-3.5">
      <SkeletonLoader.Input />
      <SkeletonLoader.Textarea />
      <SkeletonLoader.Input />
      <SkeletonLoader.Input />

      <div className="mt-auto">
        <SkeletonLoader.Button />
      </div>
    </div>
    <div className="space-y-4 bg-gray-50 p-3.5">
      <SkeletonLoader.MaterialCard />
      <SkeletonLoader.MaterialCard />
      <SkeletonLoader.MaterialCard />
      <SkeletonLoader.MaterialCard />
      <SkeletonLoader.MaterialCard />
      <SkeletonLoader.MaterialCard />
      <SkeletonLoader.MaterialCard />
    </div>
  </SidebarSplit>
);

export default EditProposalLineItemSidebar;
